import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { FiFileText, FiUpload, FiRefreshCw, FiTrash2, FiPlay, FiX } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import LLMSelector from '../JobPost/LLMSelector';
import toast from 'react-hot-toast';

const RFPComparisonForm = ({ onGenerate, isGenerating, onClear }) => {
  const [selectedModel, setSelectedModel] = useState('gpt-4');
  const [rfpFiles, setRfpFiles] = useState([null, null, null]);
  const [activeRfp, setActiveRfp] = useState(0);

  const { register, handleSubmit, formState: { errors }, watch, reset, setValue, getValues } = useForm({
    defaultValues: {
      rfp1: '',
      rfp2: '',
      rfp3: '',
      analysisType: 'comprehensive',
      focusAreas: ['pricing', 'timeline', 'technical', 'experience'],
      notes: ''
    }
  });

  const watchedValues = watch();
  const hasContent = watchedValues.rfp1?.trim() || watchedValues.rfp2?.trim() || watchedValues.rfp3?.trim();

  const sampleRFPs = {
    rfp1: `RFP Response - AlphaTech Solutions

Company: AlphaTech Solutions Inc.
Contact: Sarah Johnson, Business Development Manager
Email: sarah.johnson@alphatech.com
Phone: (555) 123-4567

EXECUTIVE SUMMARY
AlphaTech Solutions is pleased to submit our proposal for the City of Metropolis Municipal Software Platform project. With over 15 years of experience in municipal technology solutions, we offer a comprehensive, custom-built platform tailored specifically to your city's unique requirements.

PROPOSED SOLUTION
We propose developing a fully integrated municipal management platform that includes:
- Citizen services portal with online permit applications
- Financial management and budget tracking system
- Asset and infrastructure management module
- HR and payroll integration
- Real-time reporting and analytics dashboard
- Mobile-responsive design for field operations

TECHNICAL APPROACH
Our solution will be built using:
- Microsoft .NET Core framework for scalability
- Azure cloud infrastructure for reliability and security
- SQL Server database with automated backups
- RESTful APIs for third-party integrations
- Progressive Web App (PWA) technology for mobile access
- Single sign-on (SSO) integration with existing city systems

PRICING STRUCTURE
Total Project Cost: $485,000
Breakdown:
- Discovery and Requirements Analysis: $45,000
- System Design and Architecture: $65,000
- Development Phase 1 (Core Modules): $185,000
- Development Phase 2 (Advanced Features): $125,000
- Testing and Quality Assurance: $35,000
- Training and Documentation: $30,000

Annual Maintenance: $58,200 (12% of development cost)
Hosting (Azure): $1,200/month

TIMELINE
Project Duration: 14 months
Phase 1 (Months 1-3): Discovery, Design, and Planning
Phase 2 (Months 4-9): Core Development
Phase 3 (Months 10-12): Advanced Features Development
Phase 4 (Months 13-14): Testing, Training, and Go-Live

EXPERIENCE AND REFERENCES
- 50+ municipal clients across North America
- Recently completed similar project for City of Springfield (population 85,000)
- Certified Microsoft Gold Partner
- ISO 27001 security certification
- References available upon request

KEY DIFFERENTIATORS
- 24/7 technical support with guaranteed 4-hour response time
- Dedicated project manager and development team
- On-site training for up to 20 city staff members
- 5-year warranty on all custom development
- Compliance with WCAG 2.1 AA accessibility standards
- Built-in disaster recovery and business continuity features

PAYMENT TERMS
- 25% upon contract signing
- 25% upon completion of Phase 1
- 25% upon completion of Phase 2
- 25% upon final acceptance and go-live`,

    rfp2: `RFP Response - BetaSoft Municipal Systems

Company: BetaSoft Municipal Systems
Contact: Michael Chen, Solutions Director
Email: m.chen@betasoft.com
Phone: (555) 987-6543

EXECUTIVE SUMMARY
BetaSoft Municipal Systems presents our proven GovCloud platform as the optimal solution for the City of Metropolis. Our pre-configured municipal software has been successfully deployed in over 200 cities, offering rapid implementation and proven reliability.

PROPOSED SOLUTION
GovCloud Municipal Platform includes:
- Pre-built citizen engagement portal
- Integrated financial management system
- Permit and licensing management
- Code enforcement tracking
- Public works management
- Built-in reporting and analytics
- Mobile applications for citizens and staff

TECHNICAL APPROACH
Our solution leverages:
- Cloud-native architecture on AWS
- PostgreSQL database with automatic scaling
- React-based user interface
- Microservices architecture for flexibility
- API-first design for easy integrations
- Multi-tenant security model
- Automated backup and disaster recovery

PRICING STRUCTURE
Software License: $125,000 (one-time)
Implementation Services: $85,000
Data Migration: $25,000
Training (Virtual): $15,000
Total Initial Investment: $250,000

Annual Subscription: $45,000
Includes:
- Software updates and new features
- Cloud hosting and maintenance
- Technical support (business hours)
- Data backup and security monitoring

TIMELINE
Implementation: 6 months
Month 1: Project kickoff and requirements validation
Month 2-3: System configuration and customization
Month 4: Data migration and testing
Month 5: User acceptance testing and training
Month 6: Go-live and post-launch support

EXPERIENCE AND REFERENCES
- 200+ municipal implementations
- Average implementation time: 4-6 months
- 98% customer satisfaction rating
- Serving cities from 5,000 to 500,000 population
- Recent implementations: City of Riverside, Town of Brookfield

KEY DIFFERENTIATORS
- Fastest implementation in the industry
- Pre-built municipal workflows and processes
- Comprehensive training program with certification
- 99.9% uptime guarantee
- Regular feature updates at no additional cost
- Dedicated customer success manager
- Community user forum and knowledge base

PAYMENT TERMS
- 50% upon contract execution
- 30% upon successful configuration completion
- 20% upon go-live and acceptance`,

    rfp3: `RFP Response - GammaTech Municipal Solutions

Company: GammaTech Municipal Solutions LLC
Contact: Jennifer Rodriguez, Municipal Practice Lead
Email: j.rodriguez@gammatech.com
Phone: (555) 456-7890

EXECUTIVE SUMMARY
GammaTech Municipal Solutions proposes a hybrid approach combining our established CityManager platform with custom modules specifically designed for Metropolis. This approach balances proven functionality with tailored solutions for your unique requirements.

PROPOSED SOLUTION
Our hybrid solution includes:
- Core CityManager platform (proven base)
- Custom citizen services module for Metropolis
- Integrated document management system
- Advanced analytics and reporting engine
- Mobile-first design approach
- Workflow automation engine
- Integration hub for existing city systems

TECHNICAL APPROACH
Technology stack includes:
- Java Spring Boot backend architecture
- Oracle database with clustering support
- Angular frontend framework
- Docker containerization for scalability
- Kubernetes orchestration
- OAuth 2.0 security implementation
- Elasticsearch for advanced search capabilities

PRICING STRUCTURE
Base Platform License: $180,000
Custom Development: $155,000
Integration Services: $65,000
Professional Services: $45,000
Total Project Cost: $445,000

Annual Support and Maintenance: $48,000
Cloud Hosting (Optional): $800/month
Premium Support (24/7): Additional $15,000/year

TIMELINE
Project Duration: 10 months
Phase 1 (Months 1-2): Requirements and Custom Design
Phase 2 (Months 3-6): Platform Setup and Custom Development
Phase 3 (Months 7-8): Integration and Testing
Phase 4 (Months 9-10): Training and Deployment

EXPERIENCE AND REFERENCES
- 12 years in municipal software development
- 75+ municipal clients across various sizes
- Expertise in hybrid solutions combining COTS and custom development
- Strong track record in system integrations
- Recent success: City of Maplewood integration project

KEY DIFFERENTIATORS
- Hybrid approach reduces risk while meeting specific needs
- Extensive integration experience with legacy systems
- Agile development methodology with regular client feedback
- Comprehensive change management support
- Post-implementation optimization services
- Flexible hosting options (cloud, on-premise, or hybrid)
- Advanced security features including encryption at rest and in transit

PAYMENT TERMS
- 30% upon contract signing
- 30% upon Phase 2 completion
- 25% upon successful testing completion
- 15% upon final acceptance and go-live

ADDITIONAL SERVICES
- Change management consulting: $25,000
- Extended training program: $20,000
- Performance optimization (Year 1): $30,000
- Custom reporting development: $15,000 per report`
  };

  const handleFileUpload = (event, rfpIndex) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target.result;
          setValue(`rfp${rfpIndex + 1}`, content);
          const newFiles = [...rfpFiles];
          newFiles[rfpIndex] = file;
          setRfpFiles(newFiles);
          toast.success(`RFP ${rfpIndex + 1} uploaded successfully!`);
        };
        reader.readAsText(file);
      } else {
        toast.error('Please upload a .txt file. PDF extraction coming soon!');
      }
    }
  };

  const handleLoadSample = () => {
    setValue('rfp1', sampleRFPs.rfp1);
    setValue('rfp2', sampleRFPs.rfp2);
    setValue('rfp3', sampleRFPs.rfp3);
    toast.success('Sample RFPs loaded successfully!');
  };

  const handleClearRfp = (rfpIndex) => {
    setValue(`rfp${rfpIndex + 1}`, '');
    const newFiles = [...rfpFiles];
    newFiles[rfpIndex] = null;
    setRfpFiles(newFiles);
    toast.success(`RFP ${rfpIndex + 1} cleared!`);
  };

  // Handle tab switching - this is the key fix
  const handleTabSwitch = (tabIndex) => {
    setActiveRfp(tabIndex);
  };

  const onSubmit = (data) => {
    const formData = {
      ...data,
      selectedModel
    };
    onGenerate(formData);
  };

  const handleClear = () => {
    reset();
    setRfpFiles([null, null, null]);
    onClear();
    toast.success('Form cleared!');
  };

  const focusAreaOptions = [
    { id: 'pricing', label: 'Pricing & Payment Terms' },
    { id: 'timeline', label: 'Timeline & Milestones' },
    { id: 'technical', label: 'Technical Approach' },
    { id: 'experience', label: 'Experience & References' },
    { id: 'support', label: 'Support & Maintenance' },
    { id: 'deliverables', label: 'Key Deliverables' }
  ];

  // Get the current RFP content for the active tab
  const getCurrentRfpContent = () => {
    return watchedValues[`rfp${activeRfp + 1}`] || '';
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
        <h2 className="text-xl font-semibold flex items-center">
          <SafeIcon icon={FiFileText} className="mr-2" />
          RFP Comparison Details
        </h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
        {/* RFP Inputs */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Request For Proposals (Up to 3)
            </label>
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleLoadSample}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <SafeIcon icon={FiPlay} className="mr-2 h-4 w-4" />
              Load Sample RFPs
            </motion.button>
          </div>

          {/* RFP Tabs */}
          <div className="flex border-b border-gray-200">
            {[0, 1, 2].map((index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleTabSwitch(index)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeRfp === index
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                RFP {index + 1}
                {watchedValues[`rfp${index + 1}`]?.trim() && (
                  <div className="inline-block w-2 h-2 bg-green-500 rounded-full ml-2"></div>
                )}
              </button>
            ))}
          </div>

          {/* Active RFP Content */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label htmlFor={`rfp${activeRfp + 1}`} className="block text-sm font-medium text-gray-700">
                RFP {activeRfp + 1} Content {activeRfp < 2 && '*'}
              </label>
              {watchedValues[`rfp${activeRfp + 1}`]?.trim() && (
                <button
                  type="button"
                  onClick={() => handleClearRfp(activeRfp)}
                  className="text-red-600 hover:text-red-700 transition-colors"
                >
                  <SafeIcon icon={FiX} className="h-4 w-4" />
                </button>
              )}
            </div>

            <textarea
              key={activeRfp} // This key forces re-render when tab changes
              id={`rfp${activeRfp + 1}`}
              rows={12}
              value={getCurrentRfpContent()} // Use controlled value
              onChange={(e) => setValue(`rfp${activeRfp + 1}`, e.target.value)} // Handle changes
              {...register(`rfp${activeRfp + 1}`, {
                required: activeRfp < 2 ? `RFP ${activeRfp + 1} content is required` : false,
                minLength: {
                  value: 100,
                  message: `RFP ${activeRfp + 1} must be at least 100 characters long`
                }
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-none font-mono text-sm"
              placeholder={`Paste RFP ${activeRfp + 1} content here or upload a file below...`}
            />

            {/* File Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-indigo-400 transition-colors">
              <div className="text-center">
                <SafeIcon icon={FiUpload} className="mx-auto h-6 w-6 text-gray-400 mb-2" />
                <label htmlFor={`file-upload-${activeRfp}`} className="cursor-pointer">
                  <span className="text-sm text-indigo-600 hover:text-indigo-500 font-medium">
                    Upload RFP {activeRfp + 1} file
                  </span>
                  <input
                    id={`file-upload-${activeRfp}`}
                    type="file"
                    accept=".txt,.pdf,text/plain"
                    onChange={(e) => handleFileUpload(e, activeRfp)}
                    className="sr-only"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Supports .txt files (PDF support coming soon)
                </p>
                {rfpFiles[activeRfp] && (
                  <p className="text-xs text-green-600 mt-1">
                    File loaded: {rfpFiles[activeRfp].name}
                  </p>
                )}
              </div>
            </div>

            {errors[`rfp${activeRfp + 1}`] && (
              <p className="text-sm text-red-600">{errors[`rfp${activeRfp + 1}`].message}</p>
            )}
          </div>
        </div>

        {/* Analysis Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700">Analysis Configuration</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="analysisType" className="block text-sm font-medium text-gray-700">
                Analysis Type
              </label>
              <select
                id="analysisType"
                {...register('analysisType')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              >
                <option value="comprehensive">Comprehensive Analysis</option>
                <option value="technical">Technical Focus</option>
                <option value="financial">Financial Focus</option>
                <option value="timeline">Timeline Focus</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Focus Areas
              </label>
              <div className="grid grid-cols-2 gap-2">
                {focusAreaOptions.map((option) => (
                  <div key={option.id} className="flex items-center">
                    <input
                      id={`focus-${option.id}`}
                      type="checkbox"
                      value={option.id}
                      {...register('focusAreas')}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`focus-${option.id}`} className="ml-2 block text-sm text-gray-700">
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div className="space-y-2">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
            Additional Analysis Notes
          </label>
          <textarea
            id="notes"
            rows={3}
            {...register('notes')}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-none"
            placeholder="Any specific criteria or concerns to focus on during the analysis..."
          />
        </div>

        {/* LLM Selector */}
        <LLMSelector selectedModel={selectedModel} onModelChange={setSelectedModel} />

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
          <motion.button
            whileHover={{ scale: hasContent && !isGenerating ? 1.02 : 1 }}
            whileTap={{ scale: hasContent && !isGenerating ? 0.98 : 1 }}
            type="submit"
            disabled={!hasContent || isGenerating}
            className="flex-1 flex items-center justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Analyzing RFPs...
              </>
            ) : (
              <>
                <SafeIcon icon={FiRefreshCw} className="mr-2 h-4 w-4" />
                Analyze RFPs
              </>
            )}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={handleClear}
            className="px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
          >
            <SafeIcon icon={FiTrash2} className="h-4 w-4" />
          </motion.button>
        </div>
      </form>
    </div>
  );
};

export default RFPComparisonForm;